import React from 'react';
import type { ProviderDetail, ProviderLicense } from '../../../../../js/api';
import { LicenseTypeEnum, StatusEnum } from '../../../../../js/api';
import { Badge } from '../../../components/badge';
import { getDateTemporalityClass } from '../../../utils/date';
import {
  EMPTY_VALUE,
  formatProviderDate,
  labelFor,
  labelWithoutCode,
  ProviderDrawerMaps,
  textOrDash,
  yesNo,
} from './providerDisplay';
import { FilePresenceIcon, RecordCard, RecordSection } from './ProviderDrawerPrimitives';

/** Expiration dates get the shared near-expiry / expired colouring. */
export const ExpirationDate: React.FC<{ value: string | null | undefined }> = ({ value }) =>
  value ? (
    <span className={getDateTemporalityClass(value)}>{formatProviderDate(value)}</span>
  ) : (
    <>{EMPTY_VALUE}</>
  );

const LicenseStatusBadge: React.FC<{ status: string; map?: Record<string, string> }> = ({
  status,
  map,
}) => (
  <Badge color={status === StatusEnum.ACTIVE ? 'green' : 'zinc'}>{labelFor(status, map)}</Badge>
);

const sortByState = (licenses: ProviderLicense[]): ProviderLicense[] =>
  [...licenses].sort((a, b) => (a.state || '').localeCompare(b.state || ''));

function LicenseSection({
  title,
  caption,
  licenses,
  maps,
  testId,
}: {
  title: string;
  caption: string;
  licenses: ProviderLicense[];
  maps: ProviderDrawerMaps;
  testId: string;
}) {
  return (
    <RecordSection
      title={title}
      count={licenses.length}
      emptyText={`No ${title} registered.`}
      data-testid={testId}
    >
      {sortByState(licenses).map((license) => (
        <RecordCard
          key={license.id}
          // The state code IS the identity of a license ("TN License"); the
          // long state name adds nothing, so there is no separate State row.
          title={`${license.state || '??'} ${caption}`}
          rightElement={
            <span className="tw:flex tw:items-center tw:gap-2">
              <FilePresenceIcon hasFile={license.has_file} />
              <LicenseStatusBadge status={license.status} map={maps.LicenseStatus} />
            </span>
          }
          fields={[
            { label: 'Number', value: textOrDash(license.number) },
            { label: 'Issued', value: formatProviderDate(license.issue_date) },
            { label: 'Expires', value: <ExpirationDate value={license.expiration_date} /> },
          ]}
        />
      ))}
    </RecordSection>
  );
}

/** Licenses, board certifications and life-support certifications (data only, no files). */
export function LicensesTab({
  provider,
  maps,
}: {
  provider: ProviderDetail;
  maps: ProviderDrawerMaps;
}) {
  const licenses = provider.licenses || [];
  const byType = (type: LicenseTypeEnum) => licenses.filter((license) => license.type === type);
  const otherLicenses = byType(LicenseTypeEnum.OTHER);

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <LicenseSection
        title="Medical License"
        caption="Medical License"
        licenses={byType(LicenseTypeEnum.MEDICAL)}
        maps={maps}
        testId="provider-medical-licenses"
      />
      <LicenseSection
        title="DEA License"
        caption="DEA License"
        licenses={byType(LicenseTypeEnum.DEA)}
        maps={maps}
        testId="provider-dea-licenses"
      />
      <LicenseSection
        title="CSR License"
        caption="CSR License"
        licenses={byType(LicenseTypeEnum.CSR)}
        maps={maps}
        testId="provider-csr-licenses"
      />
      {otherLicenses.length > 0 && (
        <LicenseSection
          title="Other Licenses"
          caption="License"
          licenses={otherLicenses}
          maps={maps}
          testId="provider-other-licenses"
        />
      )}
      <RecordSection
        title="Board Certifications"
        count={provider.board_certifications.length}
        emptyText="No board certifications registered."
        data-testid="provider-board-certifications"
      >
        {provider.board_certifications.map((certification) => (
          <RecordCard
            key={certification.id}
            title={`${certification.type} Board Cert`}
            rightElement={
              <span className="tw:flex tw:items-center tw:gap-2">
                <FilePresenceIcon hasFile={certification.has_file} />
                {certification.board_eligible && <Badge color="sky">Board eligible</Badge>}
              </span>
            }
            fields={[
              {
                label: 'Type',
                value: labelWithoutCode(certification.type, maps.BoardCertificationType),
                stackLabel: true,
              },
              { label: 'Board Eligible', value: yesNo(certification.board_eligible) },
              { label: 'Issued', value: formatProviderDate(certification.issue_date) },
              { label: 'Expires', value: <ExpirationDate value={certification.expiration_date} /> },
            ]}
          />
        ))}
      </RecordSection>
      <RecordSection
        title="Life Support Certifications"
        count={provider.certifications.length}
        emptyText="No certifications registered."
        data-testid="provider-certifications"
      >
        {provider.certifications.map((certification) => (
          <RecordCard
            key={certification.id}
            title={`${certification.type} Life Support`}
            rightElement={<FilePresenceIcon hasFile={certification.has_file} />}
            fields={[
              {
                label: 'Type',
                value: labelWithoutCode(certification.type, maps.CertificationType),
                stackLabel: true,
              },
              { label: 'Issued', value: formatProviderDate(certification.issue_date) },
              { label: 'Expires', value: <ExpirationDate value={certification.expiration_date} /> },
            ]}
          />
        ))}
      </RecordSection>
    </div>
  );
}
